import { Merchant } from '@database/types';
import { ClaimCategory, SalesChannel } from '@pulse/constants/claims';
import { resolveProductSourceIds } from '@pulse/helpers/product-source-id.helpers';
import ClaimService from '@pulse/services/ClaimService';
import OrderService from '@pulse/services/OrderService';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const DEFAULT_WARRANTY_CLAIM = {
  category: null,
  customer: {
    emailAddress: null,
    phoneNumber: null,
  },
  dateOfPurchase: null,
  externalOrderNumber: null,
  externalSalesChannel: null,
  items: [],
  productDescription: null,
  receiptImages: [],
  receiptRequired: true,
  resolution: {
    desiredMethod: null,
  },
  shippingAddress: null,
  signature: null,
  warrantyPeriod: null,
};

export const useInStore = defineStore('conciergeInStore', () => {
  const claim = ref(DEFAULT_WARRANTY_CLAIM);
  const receiptItems = ref([]);
  // Only ever true when a receipt actually exists to read, so a claim without one never waits.
  const isReadingReceipt = ref(false);

  // Keyed by the receipt it was started for, so changing the photos re-reads while arriving at the
  // products page twice does not. Holds the in-flight promise, not a boolean: the products page can
  // land while the read is still running.
  let receiptRead = { key: null, promise: null };
  const config = useRuntimeConfig();
  // apiBaseUrl is now the proxy prefix itself, so appending /api here would produce /api/api.
  const orderService = new OrderService(config.public.apiBaseUrl);
  const service = new ClaimService(config.public.apiBaseUrl);

  function setClaim(claimData) {
    claim.value = claimData;
    receiptItems.value = [];
    isReadingReceipt.value = false;
    receiptRead = { key: null, promise: null };
  }

  /**
   * setShippingAddress
   * updates the claim.shippingAddress with the provided address
   * @param {Object} address
   */
  function setShippingAddress(address) {
    claim.value.shippingAddress = address;
  }

  /**
   * setEmailAddress
   * updates the claim.customer.emailAddress with the provided email address
   * @param {Object} address
   */
  function setEmailAddress(emailAddress) {
    claim.value.customer.emailAddress = emailAddress;
  }

  /**
   * Earns the guest session the rest of the in-store flow carries. Throws rather than swallowing:
   * the offline claim route requires the session, so continuing without one only moves the failure to
   * submit — after the customer has entered an address, items, a receipt and a signature. Failing here
   * costs them one step. The service response stores the token, so nothing is returned.
   */
  async function createSession() {
    const storeId = claim.value.store?.id;
    const email = claim.value.customer?.emailAddress;

    if (!storeId || !email) throw new Error('A store and an email are required to start an in-store claim');

    await service.createInStoreSession({ email, storeId });
  }

  /**
   * setWarrantyPeriod
   * sets the warranty period with the provided period
   * @param {Object} address
   */
  function setWarrantyPeriod(warrantyPeriod) {
    claim.value.warrantyPeriod = warrantyPeriod;
  }

  /**
   * setProductDescription
   * sets the productDescription on the most recently added item.
   * Per-item because shipping claims can have multiple "not found" items, each with its
   * own description. Warranty claims have one item, so it lands on that item.
   */
  function setProductDescription(description) {
    const items = claim.value.items;
    if (!items.length) return;
    const idx = items.length - 1;
    items[idx] = { ...items[idx], productDescription: description };
    claim.value.items = items;
  }

  /**
   * Reads the products off the uploaded receipt so the "I can't find it" form arrives filled in.
   *
   * Started only when the customer says they cannot find their product, not when they upload the
   * receipt. Most never get that far, and a vision call on every uploaded receipt would pay for all
   * of them. Never awaited by a page and never rejects — a receipt we could not read just leaves the
   * form as empty as it is today, and the answer fills in whenever it lands.
   */
  function readReceipt() {
    const storeId = claim.value.store?.id;
    const imageUrls = claim.value.receiptImages ?? [];

    if (!storeId || !imageUrls.length) return Promise.resolve();

    const key = imageUrls.join('|');
    if (receiptRead.key === key && receiptRead.promise) return receiptRead.promise;

    isReadingReceipt.value = true;

    const promise = orderService
      .readReceipt({ imageUrls, storeId })
      .then(result => {
        receiptItems.value = result.items ?? [];
      })
      .catch(() => {
        receiptItems.value = [];
      })
      .finally(() => {
        isReadingReceipt.value = false;
      });

    receiptRead = { key, promise };

    return promise;
  }

  /**
   * setStore
   * sets the store the in store order is related to
   * @param {Object} address
   */
  function setStore(store) {
    claim.value.store = store;
  }

  /**
   * setReceiptImages
   * sets the receipt images uploaded
   * @param {Object} address
   */
  function setReceiptImages(images) {
    claim.value.receiptImages = images;
  }

  /**
   * setPhoneNumber
   * updates the claim.customer.phoneNumber with the provided phone number
   * @param {Object} address
   */
  function setPhoneNumber(phoneNumber) {
    claim.value.customer.phoneNumber = phoneNumber === '' ? null : phoneNumber;
  }

  /**
   * setDateOfPurchase
   * updates the claim.dateOfPurchase with the provided date
   * @param {Object} address
   */
  function setDateOfPurchase(date) {
    claim.value.dateOfPurchase = date;
  }

  function addSignature(data) {
    claim.value.signature = data;
  }

  function transformStoreToInStoreWarrantyClaim() {
    const isShipping = claim.value.category === ClaimCategory.SHIPPING;
    const items = claim.value.items.map(entry => {
      const { productId, variantId } = getProductIdAndVariantId(entry);

      return {
        details: entry.details,
        images: entry.images ?? [],
        // ENG-5872 — how the customer arrived at this product. Dropped here until now, which left
        // the ENG-5794 validation reconstructing from scratch what the search step already knew.
        isSubstitute: entry.item.isSubstitute ?? false,
        issueType: entry.issueType,
        matchScore: entry.item.matchScore ?? null,
        name: entry.item.name,
        originalProductName: entry.item.originalProductName ?? null,
        price: entry.item.price,
        productDescription: entry.productDescription ?? null,
        productImage: entry.item.productImage,
        quantity: entry.quantity ?? 1,
        // ENG-5874 — what they agreed to receive, flattened for the DTO. Never replaces the item
        // fields above: the claim is about the thing they own.
        replacementSourceProductId: entry.item.replacement?.sourceProductId ?? null,
        replacementSourceVariantId: entry.item.replacement?.sourceVariantId ?? null,
        selectionSource: entry.item.selectionSource ?? null,
        sku: entry.item.sku,
        sourceItemId: null,
        sourceProductId: productId,
        sourceVariantId: variantId,
        subIssueId: entry.subIssueId,
        substituteBasis: entry.item.matchBasis ?? [],
        warrantyPeriod: isShipping ? null : (entry.warrantyPeriod ?? claim.value.warrantyPeriod ?? null),
      };
    });

    // Claim-level type mirrors the backend: MULTIPLE when items carry different issue types.
    const itemIssueTypes = [...new Set(claim.value.items.map(entry => entry.issueType).filter(Boolean))];
    const claimIssueType = itemIssueTypes.length > 1 ? 'MULTIPLE' : (itemIssueTypes[0] ?? null);

    return {
      claimCategory: claim.value.category ?? ClaimCategory.WARRANTY,
      customer: {
        email: claim.value.customer.emailAddress,
        phoneNumber: claim.value.customer.phoneNumber,
      },
      externalOrderNumber: claim.value.externalOrderNumber,
      externalSalesChannel: claim.value.externalSalesChannel,
      issueType: claimIssueType,
      items,
      resolution: claim.value.resolution.desiredMethod,
      salesChannel: SalesChannel.IN_STORE,
      shippingAddress: claim.value.shippingAddress,
      signature: claim.value.signature,
      storeId: claim.value.store.id,
      warranty: {
        dateOfPurchase: claim.value.dateOfPurchase,
        receiptImages: claim.value.receiptImages,
        receiptRequired: receiptRequired.value,
      },
    };
  }

  function getProductIdAndVariantId(entry) {
    const { sourceProductId, sourceVariantId } = resolveProductSourceIds(entry.item);

    return { productId: sourceProductId, variantId: sourceVariantId };
  }

  function submitClaim() {
    const transformed = transformStoreToInStoreWarrantyClaim();

    return service.createInStoreClaim(transformed);
  }

  function addItem(item) {
    claim.value.items = [...claim.value.items, item];
  }

  function removeItem(index) {
    claim.value.items = claim.value.items.filter((_, i) => i !== index);
  }

  function setItems(items) {
    claim.value.items = items;
  }

  function setClaimCategory(category) {
    claim.value.category = category;
    claim.value.items = [];
    claim.value.externalSalesChannel = null;
    claim.value.externalOrderNumber = null;
  }

  // Returns false when the customer must pick on the claim-type page.
  function ensureClaimCategory() {
    if (claim.value.category) return true;

    const offline = claim.value.store?.offlineClaimSettings;
    const enabled = [
      offline?.shippingClaimsEnabled && ClaimCategory.SHIPPING,
      offline?.warrantyClaimsEnabled && ClaimCategory.WARRANTY,
    ].filter(Boolean);
    if (enabled.length !== 1) return false;

    setClaimCategory(enabled[0]);

    return true;
  }

  function setExternalSalesChannel(channel) {
    claim.value.externalSalesChannel = channel;
  }

  function setExternalOrderNumber(orderNumber) {
    claim.value.externalOrderNumber = orderNumber;
  }

  const receiptRequired = computed(() => {
    return claim.value.store?.offlineClaimSettings?.receiptRequired ?? true;
  });

  const dateOfPurchase = computed(() => {
    return claim.value.dateOfPurchase;
  });

  const warrantyPeriod = computed(() => {
    return claim.value.warrantyPeriod;
  });

  const store = computed(() => {
    return claim.value.store;
  });

  const coverageRules = computed(() => {
    return claim.value.store?.coverageRules ?? [];
  });

  // In-store has no order/policy to read warranty policy types from (unlike online). Derive the
  // set the store actually offers from its coverage rules so per-item coverage gating isn't forced
  // to fall back to checking both manufacturer + extended warranty.
  const warrantyPolicyTypes = computed(() => {
    const warrantyTypes = [Merchant.PolicyType.MANUFACTURER_WARRANTY, Merchant.PolicyType.EXTENDED_WARRANTY];

    return [
      ...new Set(coverageRules.value.map(rule => rule.category).filter(category => warrantyTypes.includes(category))),
    ];
  });

  const currentClaim = computed(() => {
    return claim.value;
  });

  function searchProducts(title, storeId) {
    return orderService.getProducts(title, storeId);
  }

  async function getClaimSubtypes(storeId) {
    const claimSubtypes = await service.getClaimSubtypes(storeId, ClaimCategory.WARRANTY);
    claim.value.claimSubtypes = claimSubtypes.data;
  }

  function setRequestedResolution(resolution) {
    claim.value.resolution.desiredMethod = resolution;
  }

  return {
    addItem,
    addSignature,
    coverageRules,
    createSession,
    currentClaim,
    dateOfPurchase,
    ensureClaimCategory,
    getClaimSubtypes,
    isReadingReceipt,
    readReceipt,
    receiptItems,
    receiptRequired,
    removeItem,
    searchProducts,
    setClaim,
    setClaimCategory,
    setDateOfPurchase,
    setEmailAddress,
    setExternalOrderNumber,
    setExternalSalesChannel,
    setItems,
    setPhoneNumber,
    setProductDescription,
    setReceiptImages,
    setRequestedResolution,
    setShippingAddress,
    setStore,
    setWarrantyPeriod,
    store,
    submitClaim,
    warrantyPeriod,
    warrantyPolicyTypes,
  };
});
